// A LEITURA de um diagnóstico REAL: as linhas de cada dimensão, os avisos obrigatórios e o resumo
// financeiro. Uma fonte só para as três superfícies que mostram isso (tela da web, PDF e app).
//
// Por que aqui e não em cada tela: as três montavam as mesmas linhas à mão, com os mesmos rótulos
// copiados. Quando a v4 trocou o formato das entradas (proveniência por campo, base anual, nove
// fontes), as três precisariam mudar juntas — e a que ficasse para trás não quebraria, apenas
// mostraria o número errado, calado.
//
// Referência: "Diagnóstico REAL v4", §11.3 (textos obrigatórios), §7.5 (exibição do E) e §13.2
// (diagnósticos em versão anterior).

import { dinheiroDoRelatorio, fmtNum, fmtPct, FREQ_LABELS, PAGANTE_LABELS, PREMIOS_LABELS_V3, type DimKey } from '../../constants/realCopy';
import { FIXOS } from '../../constants/realTextos';
import { ALIQUOTA_PCT, faixaDe, FAIXAS_DE_SALDO, type Aliquota, type Faixa, type FonteDeReceita, type Proveniencia, type TipoDeContratante } from './index';

/** Uma linha de dado no cartão da dimensão. `num` é formatado pelo consumidor; `valor` já vem pronto. */
export interface LinhaDoRelatorio {
  rotulo: string;
  num?: number | null;
  valor?: string;
  /** 'self' rende o "informado por você" do §11.3.2; 'api' é dado verificado; ausente não se marca. */
  fonte?: Proveniencia;
}

/** Um aviso obrigatório da interface (§11.3). A chave serve para testar sem depender do texto. */
export interface AvisoDoRelatorio { chave: string; texto: string }

// deno-lint-ignore-file no-explicit-any
type Diagnostico = Record<string, any>;

/**
 * Um diagnóstico gravado antes da v4 (§13.2).
 *
 * Os 77 anteriores não têm os dados do E anual (shows por ano, seis cachês, nove fontes, alíquota)
 * nem a autodeclaração de R. Continuam visíveis, marcados, e a saída é refazer — recalcular só a
 * parte que dá não produziria um perfil interpretável, produziria uma mistura de duas metodologias.
 */
export const ehLegado = (ri: Diagnostico | null | undefined): boolean => Number(ri?.version ?? 0) < 4;

/**
 * O diagnóstico foi feito numa versão ANTERIOR do método, qualquer que seja ela (§13.2).
 *
 * ⚠️ SÃO DOIS PREDICADOS, E SEPARÁ-LOS É O PONTO. O `ehLegado` decide o RAMO DE RENDERIZAÇÃO:
 * um v2/v3 não tem `raw`, nem `flags`, nem proveniência por campo, e cada superfície tem um ramo
 * próprio que lê o formato antigo. Este aqui decide só o AVISO e o convite a refazer.
 *
 * Marcar os 12 diagnósticos v4 gravados em produção com o `ehLegado` atirava-os para o ramo
 * v2/v3, que procura campos que um v4 não tem: sairiam `R$ 0` e `NaN` num relatório que hoje
 * está correto. Eles renderizam como o que são — v4 —, e só ganham o aviso de que o método
 * mudou desde então.
 */
export const ehVersaoAnterior = (ri: Diagnostico | null | undefined): boolean =>
  !!ri && (ehLegado(ri) || !ri.revenue?.caminho);

/** F17 (§10) — o texto é da autora, e vive com os outros em `constants/realTextos`. */
export const AVISO_LEGADO = FIXOS.F17;

/**
 * Os grupos de comentário do E que MIGRAM para a página "Onde a conta fecha" do PDF (§2).
 *
 * Eles falam de margem, ponto de equilíbrio e composição da receita, que é o assunto daquela
 * página. Sem esta lista, os dois lugares imprimiriam o mesmo parágrafo: a página do E e a do
 * aprofundamento, no mesmo documento, uma depois da outra.
 */
export const GRUPOS_DA_CONTA: readonly string[] = ['E7', 'E5', 'E8'];

/** Os textos obrigatórios do §11.3, na ordem em que a spec os lista. */
export const AVISOS = {
  travaL: 'Sua legitimação ainda não tem um sinal de plataforma. Playlist editorial ou execução em '
    + 'rádio acende esta dimensão.',
  autodeclarado: 'Adicione suas redes sociais ao seu perfil no Spotify for Artists e refaça o diagnóstico quando quiser.',
  informadoPorVoce: 'informado por você',
  saldoNegativo: 'Sua carreira consumiu mais do que gerou nos últimos 12 meses.',
  naoSei: 'Você não soube informar esta fonte. Conhecer cada receita é parte da gestão da carreira.',
  informativo: 'informativo, não entra no diagnóstico',
  semBilheteria: 'Sem shows de bilheteria como atração principal, o público real não pode ser comprovado.',
} as const;

/** Os avisos que ESTE diagnóstico precisa mostrar, já filtrados pelas flags do motor. */
export const avisosDoDiagnostico = (ri: Diagnostico | null | undefined): AvisoDoRelatorio[] => {
  if (!ri) return [];
  if (ehLegado(ri)) return [{ chave: 'legado', texto: AVISO_LEGADO }];
  const f = ri.flags ?? {};
  // Um v4 de antes da v4.5 recebe o aviso E os avisos dele. Diferente do legado, que recebe só
  // o aviso: ali as flags da v4 nem existem, e misturá-las afirmaria coisas sobre dados que
  // aquele diagnóstico nunca coletou. Aqui elas existem e continuam verdadeiras.
  const avisos: AvisoDoRelatorio[] = ehVersaoAnterior(ri)
    ? [{ chave: 'legado', texto: AVISO_LEGADO }]
    : [];
  if (f.travaL) avisos.push({ chave: 'travaL', texto: AVISOS.travaL });
  if (f.saldoNegativo) avisos.push({ chave: 'saldoNegativo', texto: AVISOS.saldoNegativo });
  if (f.aSemBilheteria) avisos.push({ chave: 'semBilheteria', texto: AVISOS.semBilheteria });
  if (Array.isArray(f.autodeclarados) && f.autodeclarados.length) {
    avisos.push({ chave: 'autodeclarado', texto: AVISOS.autodeclarado });
  }
  if (Array.isArray(f.naoSeiFontes) && f.naoSeiFontes.length) {
    avisos.push({ chave: 'naoSei', texto: AVISOS.naoSei });
  }
  return avisos;
};

/**
 * Os avisos que NÃO têm lugar próprio na entrega, e por isso precisam de um bloco no topo.
 *
 * Todos os textos do §11.3 continuam obrigatórios; o que muda é ONDE eles aparecem. Quatro deles
 * pertencem a uma dimensão e são impressos dentro do cartão dela, onde ganham o contexto que o
 * texto sozinho não tem:
 *
 *   travaL          → cartão do L
 *   saldoNegativo   → cartão do E
 *   semBilheteria   → cartão do A
 *   naoSei          → cartão do E, nomeando a fonte ("Não informado: Editora")
 *   autodeclarado   → cartão do R, que é onde moram os campos que aceitam autodeclaração
 *
 * Repetir os mesmos textos num bloco no alto, sem dizer de qual fonte ou de qual campo se trata,
 * é a mesma frase duas vezes — e a primeira, a genérica, chega antes do artista ter lido um
 * número sequer. O que sobra para o topo é o aviso de VERSÃO, que fala do documento inteiro e
 * não cabe em cartão nenhum.
 */
export const avisosSemLugarProprio = (ri: Diagnostico | null | undefined): AvisoDoRelatorio[] =>
  avisosDoDiagnostico(ri).filter((a) => a.chave === 'legado');

/** O valor e a origem de um campo de entrada da v4. Fora da v4, devolve o número cru sem origem. */
const medida = (ri: Diagnostico, campo: string): { value: number | null; fonte?: Proveniencia } => {
  const m = ri?.inputs?.[campo];
  if (m && typeof m === 'object') return { value: m.value ?? null, fonte: m.source };
  return { value: m == null ? null : Number(m) };
};

const linhaDeMedida = (ri: Diagnostico, rotulo: string, campo: string): LinhaDoRelatorio => {
  const { value, fonte } = medida(ri, campo);
  return { rotulo, num: value, fonte };
};

/** Os rótulos dos tipos de contratante, para o gráfico de cachê (§7.5). */
export const ROTULO_DO_CONTRATANTE: Record<TipoDeContratante, string> = {
  corporativos: 'Corporativos',
  orgaosPublicos: 'Órgãos públicos',
  particulares: 'Particulares',
  produtores: 'Produtores de eventos',
  casasDeShow: 'Casas de show',
  outros: 'Outros',
};

/** Os rótulos curtos das nove fontes, para a tabela de receita (§7.5). */
export const ROTULO_DA_FONTE: Record<FonteDeReceita, string> = {
  distribuidora: 'Distribuidora',
  editora: 'Editora',
  associacao: 'Associação (direitos de execução)',
  publi: 'Publis e ativações',
  patrocinios: 'Patrocínios e editais',
  aulas: 'Aulas, cursos e mentorias',
  produtos: 'Produtos físicos',
  financiamento: 'Financiamento coletivo',
  outras: 'Outras fontes',
};

export const ROTULO_DA_ALIQUOTA: Record<Aliquota, string> = {
  ate6: 'Até 6%', '6-10': 'De 6% a 10%', '10-15': 'De 10% a 15%', acima15: 'Acima de 15%', nao_sei: 'Não sei',
};

/**
 * Remuneração média mensal do setor cultural formal (SIIC/IBGE, edição vigente).
 *
 * [EXIBIÇÃO] §7.3: entra no relatório como comparação e NUNCA no índice. O corte do E vem da PNAD
 * (P95 da renda individual), que é outra coisa — misturar as duas trocaria a régua do método.
 */
export const SIIC_MENSAL = 4_658;
export const SIIC_ANUAL = SIIC_MENSAL * 12;

/**
 * O F23 sobre um valor: "cerca de R$ 96 mil".
 *
 * ⚠️ EXISTE PARA AS QUATRO SUPERFÍCIES NÃO INVENTAREM QUATRO GRAFIAS. Tela, PDF da web, PDF do
 * núcleo e app imprimem os mesmos números, e o rótulo é parte do número — separá-lo faria a mesma
 * receita sair "cerca de R$ 96 mil" num documento e "~R$ 96.000" no outro.
 *
 * `emFaixas` é o portão, e não é zelo: as compilações da loja ainda mandam reais DIGITADOS pelas
 * mesmas chaves, e esses não são aproximação de nada.
 */
export const cercaDe = (valor: number, emFaixas: boolean): string =>
  (emFaixas ? `${FIXOS.F23} ${dinheiroDoRelatorio(valor)}` : dinheiroDoRelatorio(valor));

/** O resumo financeiro da entrega (§7.5). Devolve `null` fora da v4. */
export const resumoDoE = (ri: Diagnostico | null | undefined) => {
  if (!ri || ehLegado(ri)) return null;
  const rev = ri.revenue ?? {};
  const cache = (Object.entries(rev.cacheByType ?? {}) as [TipoDeContratante, number][])
    .filter(([, v]) => Number(v) > 0)
    .map(([tipo, valor]) => ({ tipo, rotulo: ROTULO_DO_CONTRATANTE[tipo] ?? tipo, valor: Number(valor) }));
  const fontes = (Object.entries(rev.receitaOutras ?? {}) as [FonteDeReceita, { valor: number; naoSei: boolean }][])
    .filter(([, v]) => Number(v?.valor) > 0 || v?.naoSei)
    .map(([fonte, v]) => ({ fonte, rotulo: ROTULO_DA_FONTE[fonte] ?? fonte, valor: Number(v.valor) || 0, naoSei: !!v.naoSei }));
  const aliquota: Aliquota | null = rev.aliquota ?? null;
  // ⚠️ DIAGNÓSTICO ANTERIOR À v4.5 NÃO TEM `caminho`, e o padrão tem de ser 'detalhado'.
  //
  // Os 12 diagnósticos v4 gravados em produção têm receita, custos e saldo somados das parcelas —
  // que é exatamente o caminho detalhado. Assumir 'direto' fecharia a saúde financeira deles e
  // trocaria o relatório por um convite a detalhar o que eles já detalharam.
  const caminho: 'direto' | 'detalhado' = rev.caminho === 'direto' ? 'direto' : 'detalhado';
  const faixaDoSaldo: Faixa | null = caminho === 'direto' ? faixaDe(FAIXAS_DE_SALDO, rev.saldoFaixa) : null;
  return {
    /** §13.1 — 'direto' é quem respondeu só a faixa de saldo; 'detalhado' é quem abriu as parcelas. */
    caminho,
    /**
     * Os valores são pontos médios de faixa, e por isso levam o "cerca de" (F23).
     *
     * Falso num build antigo da loja, que manda as mesmas contas em reais digitados — e também no
     * diagnóstico gravado antes da v4.5, que não tem o campo.
     */
    emFaixas: rev.emFaixas === true,
    /** A faixa escolhida, com os três rótulos. `null` no caminho detalhado. */
    faixaDoSaldo,
    showsPerYear: Number(rev.showsPerYear) || 0,
    cache,
    cacheMedio: Number(rev.cacheMedio) || 0,
    receitaShows: Number(rev.receitaShows) || 0,
    fontes,
    receitaOutrasTotal: Number(rev.receitaOutrasTotal) || 0,
    receitaAnual: Number(rev.receitaAnual) || 0,
    // O investimento decomposto (v4.1, §7.5): o total continua existindo para a linha de saldo,
    // e as três parcelas alimentam o gráfico de composição do custo.
    custoPorShow: Number(rev.custoPorShow) || 0,
    custoShowsAnual: Number(rev.custoShowsAnual) || 0,
    custoFixoMensal: Number(rev.custoFixoMensal) || 0,
    custoFixoAnual: Number(rev.custoFixoAnual) || 0,
    investLancamentos12m: Number(rev.investLancamentos12m) || 0,
    investimentoAnual: Number(rev.investimentoAnual) || 0,
    saldo: Number(rev.saldo) || 0,
    bonus: Number(rev.bonus) || 1,
    saldoAjustado: Number(rev.saldoAjustado) || 0,
    aliquota,
    aliquotaRotulo: aliquota ? ROTULO_DA_ALIQUOTA[aliquota] : null,
    aliquotaPct: aliquota && aliquota !== 'nao_sei' ? ALIQUOTA_PCT[aliquota] : null,
    receitaLiquidaEstimada: rev.receitaLiquidaEstimada ?? null,
    margemPorShow: rev.margemPorShow ?? null,
    pontoEquilibrioShows: rev.pontoEquilibrioShows ?? null,
    // Quantas vezes a média do setor cultural formal (§7.3). Só comparação, nunca cálculo.
    // ⚠️ A COMPARAÇÃO É SOBRE O SALDO REAL, e não sobre a receita (v4.4, §7.10).
    //
    // Duas razões. A primeira é que o mesmo PDF trazia dois múltiplos diferentes do mesmo
    // patamar: este card dividia a RECEITA pelo salário do setor, e o comentário E8, logo ao
    // lado, dividia o saldo ajustado. Dois números para a mesma frase, na mesma página. A
    // segunda é que a comparação só é honesta assim: um salário é líquido, e o que se compara
    // com ele é o que sobra depois de a carreira pagar o que custou.
    vezesOSetor: SIIC_ANUAL > 0 ? (Number(rev.saldo) || 0) / SIIC_ANUAL : 0,
    // §7.5 — quem não tem empresário recebe a recomendação, e é o mesmo dado que dá o bônus do E.
    recomendarEmpresariamento: ri.raw?.temEmpresario === false,
  };
};

/**
 * O artista abriu as parcelas do E, e portanto há conta a mostrar (§12).
 *
 * ⚠️ AS QUATRO SUPERFÍCIES PERGUNTAM ISTO, E NÃO O CAMINHO. Tela, PDF da web, PDF do núcleo e app
 * mostram os mesmos três blocos — composição da receita, cachê por tipo e saúde financeira — e
 * cada um lia o `revenue` por conta própria. Com a pergunta escrita quatro vezes, bastava uma
 * ficar para trás para o mesmo diagnóstico mostrar "Receita R$ 0" num documento e a faixa no
 * outro, sem erro nenhum e sem nada no log.
 *
 * O legado (v2/v3) responde `true`: ele tem faturamento e investimento gravados, e cada superfície
 * já tem o ramo próprio que os lê. Fechá-lo aqui apagaria a conta de 72 diagnósticos.
 */
export const detalhouOE = (ri: Diagnostico | null | undefined): boolean =>
  resumoDoE(ri)?.caminho !== 'direto';

/**
 * O convite a detalhar (F22), ou `null` quando não há o que convidar.
 *
 * Vai onde a conta estaria: quem respondeu a faixa não vê três blocos vazios, vê a razão de não
 * os ver e o que fazer para os ter da próxima vez.
 */
export const conviteADetalhar = (ri: Diagnostico | null | undefined): string | null =>
  (detalhouOE(ri) ? null : FIXOS.F22);

/** As linhas de dado do cartão de uma dimensão (v4). Fora da v4, devolve lista vazia. */
export const linhasDaDimensao = (
  ri: Diagnostico | null | undefined,
  dim: DimKey,
  cm?: Record<string, any> | null,
): LinhaDoRelatorio[] => {
  if (!ri || ehLegado(ri)) return [];
  const bruto = ri.raw ?? {};
  const rev = ri.revenue ?? {};

  if (dim === 'r') {
    return [
      linhaDeMedida(ri, 'Ouvintes Spotify', 'spotifyListeners'),
      linhaDeMedida(ri, 'Instagram', 'igFollowers'),
      linhaDeMedida(ri, 'TikTok', 'tiktokFollowers'),
      linhaDeMedida(ri, 'YouTube (views/mês)', 'youtubeMonthlyViews'),
    ];
  }

  if (dim === 'e') {
    const shows: LinhaDoRelatorio = { rotulo: 'Shows (12 meses)', valor: String(Number(rev.showsPerYear) || 0), fonte: 'self' };

    // ⚠️ NO CAMINHO DIRETO NÃO HÁ RECEITA, CUSTO NEM MARGEM — e não é que estejam a zero: elas
    // NUNCA FORAM PERGUNTADAS (§12). Imprimi-las daria "Receita R$ 0 · Custos R$ 0 · Saldo
    // R$ 96.000", três linhas de que só a última é verdade, e as duas primeiras a acusar de
    // não faturar nada quem acabou de dizer que ganha dez mil por mês.
    //
    // Saem duas linhas: os shows, que ele respondeu, e a faixa, que é a resposta dele — o rótulo
    // mensal, que é como ele a escolheu, e o anual, que é a base em que o método lê.
    const faixa = resumoDoE(ri)?.faixaDoSaldo ?? null;
    if (rev.caminho === 'direto' && faixa) {
      return [
        shows,
        { rotulo: 'Saldo (12 meses)', valor: faixa.rotulo, fonte: 'self' },
        { rotulo: 'No ano', valor: faixa.noAno ?? faixa.rotulo, fonte: 'self' },
      ];
    }

    // A v4 lê SALDO, não receita: mostrar só o faturamento contaria a metade que agrada e
    // esconderia a que decide a dimensão.
    // §12 — a tabela do E mostra as PARCELAS do custo, não só o total. É o que permite ao artista
    // conferir a própria conta: o total sozinho não diz se o peso está no show, no fixo ou no
    // lançamento, e é essa distinção que muda a decisão.
    //
    // Os valores são pontos médios de faixa desde a v4.5, e levam o "cerca de" por isso (F23). Um
    // build antigo da loja manda as mesmas chaves em reais digitados, e esses saem sem o rótulo.
    const emFaixas = rev.emFaixas === true;
    const dinheiro = (v: unknown) => cercaDe(Number(v) || 0, emFaixas);
    const linhas: LinhaDoRelatorio[] = [
      shows,
      { rotulo: 'Receita (12 meses)', valor: dinheiro(rev.receitaAnual), fonte: 'self' },
      { rotulo: 'Custo médio por show', valor: dinheiro(rev.custoPorShow), fonte: 'self' },
      { rotulo: 'Custo fixo mensal', valor: dinheiro(rev.custoFixoMensal), fonte: 'self' },
      { rotulo: 'Investimento em lançamentos', valor: dinheiro(rev.investLancamentos12m), fonte: 'self' },
      { rotulo: 'Custos e investimento (12 meses)', valor: dinheiro(rev.investimentoAnual), fonte: 'self' },
      { rotulo: 'Saldo', valor: dinheiro(rev.saldo), fonte: 'self' },
    ];
    // ⚠️ O SALDO AJUSTADO NÃO É EXIBIDO EM LADO NENHUM (relatório v4.4, §1.3, §12 e §13 item 15).
    //
    // Ele existe, e continua a decidir a nota: ter CNPJ e ter empresário valem um bônus sobre o
    // saldo positivo. Mas é PONTUAÇÃO, e estava a ser mostrado como DINHEIRO, com o percentual do
    // bônus ao lado. Duas coisas erradas de uma vez: o artista via um valor que não existe na
    // conta bancária dele, e via um número proprietário do método, que o relatório não expõe por
    // princípio. O efeito do bônus aparece onde deve aparecer, na nota e no estado da dimensão.
    return linhas;
  }

  if (dim === 'a') {
    const conv = ri.components?.a?.find((c: any) => c.key === 'conversion');
    const listeners = medida(ri, 'spotifyListeners').value;
    const followers = medida(ri, 'spotifyFollowers').value;
    return [
      {
        rotulo: 'Conversão (seguidores ÷ ouvintes)',
        // Relatório §11/§12: "1 para N", arredondado, e nunca em porcentagem.
        valor: conv?.present && listeners && followers
          ? `1 para ${Math.max(1, Math.round(listeners / followers))}`
          : 'Sem dado',
        fonte: conv?.present ? 'api' : 'absent',
      },
      { rotulo: 'Shows (12 meses)', valor: String(Number(rev.showsPerYear) || 0), fonte: 'self' },
      {
        rotulo: 'Público pagante',
        // v4.8 (§4): zero show é "não faz bilheteria", mesmo quando o diagnóstico antigo gravou "sim".
        valor: bruto.fazBilheteria && (Number(rev.showsPerYear) || 0) > 0 ? (PAGANTE_LABELS[bruto.pagantePct] ?? '—') : 'Não faz bilheteria',
        fonte: 'self',
      },
      linhaDeMedida(ri, 'Seguidores Spotify', 'spotifyFollowers'),
    ];
  }

  const l = ri.components?.l ?? {};
  const playlists = l.playlists ?? {};
  const radio = l.radio ?? {};
  const execucoes = ri.raw?.radioAirplay180d;
  return [
    { rotulo: 'Prêmios', valor: PREMIOS_LABELS_V3[Number(bruto.premios ?? 0)] ?? '—', fonte: 'self' },
    {
      rotulo: 'Imprensa',
      valor: bruto.imprensaRepercussao ? (FREQ_LABELS[bruto.imprensaFrequencia] ?? 'Sim') : 'Não',
      fonte: 'self',
    },
    {
      rotulo: 'Playlists editoriais',
      // Consulta vazia ≠ consulta que não aconteceu (§4): a primeira é um zero que pesa, a
      // segunda é ausência que renormaliza os pesos. Dizer "0" nas duas apagaria a diferença.
      valor: playlists.present
        ? String(bruto.editorialPlaylists ?? cm?.playlists?.count ?? 0)
        : 'Sem dado',
      fonte: playlists.present ? 'api' : 'absent',
    },
    {
      rotulo: 'Execução em rádio',
      // Abaixo de 6 execuções em 180 dias o componente é AUSENTE, não zero (§9.5). Mostrar o
      // número mede a presença; "Sim" esconderia o tamanho dela.
      valor: radio.present && execucoes != null
        ? `${fmtNum(Math.round(Number(execucoes)))} execuções`
        : 'Sem dado',
      fonte: radio.present ? 'api' : 'absent',
    },
  ];
};

/**
 * O que o card "shows pra cobrir o fixo do ano" mostra (§7.9).
 *
 * ⚠️ SEM CUSTO FIXO, A CONTA FECHA — e o card dizia o contrário. Ele lia o ponto de equilíbrio,
 * que é nulo tanto para quem NÃO TEM fixo a cobrir (a conta mais simples que existe: cada show
 * já entra como saldo) como para quem tem margem negativa (a conta que não fecha nunca). Os dois
 * casos saíam como "não fecha", e o primeiro é exatamente o oposto disso.
 *
 * Mora aqui, e não nas telas, porque a web e o PDF mostram o mesmo card: escrita duas vezes, a
 * regra divergia no primeiro ajuste.
 */
export const equilibrioExibido = (
  conta: { margemPorShow: number | null; custoFixoAnual: number; pontoEquilibrioShows: number | null },
): string => {
  if (conta.pontoEquilibrioShows != null) return String(conta.pontoEquilibrioShows);
  const margem = conta.margemPorShow ?? 0;
  return margem > 0 && !conta.custoFixoAnual ? 'fecha' : 'não fecha';
};

/** O engajamento, quando a API entregou. [SUSPENSO] no cálculo, exibido com rótulo (§8.2, §11.3.5). */
/** Uma linha da secção "Sua presença nas plataformas" (§12, secção 9). */
export interface LinhaDaPlataforma {
  rotulo: string;
  valor: string;
  /** Sai com o rótulo do §8.5: "informativo, não entra no diagnóstico". */
  informativo?: boolean;
}

const NOME_DA_REDE: Record<string, string> = {
  instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube',
};

/**
 * O conteúdo da secção "Sua presença nas plataformas" (§12, secção 9).
 *
 * ⚠️ ELA CONTRADIZIA O L, E É POR ISSO QUE MUDA. O bloco "Imprensa em detalhe" que vivia aqui
 * escrevia, para quem respondeu "não" à repercussão, que "a imprensa ainda não repercutiu o seu
 * trabalho" — um parágrafo escrito à mão, num documento onde a dimensão L já diz o que a matriz
 * de veículos apurou, com os textos da autora. Dois donos da mesma afirmação, e o segundo era o
 * que não olhava a matriz: bastava marcar um veículo e responder "não" à pergunta anterior para
 * o PDF negar, em prosa, o que a página do L tinha acabado de reconhecer.
 *
 * No lugar entram os SINAIS DE PLATAFORMA, que é o que a secção se propõe a mostrar: playlists
 * editoriais e rádio, que entram no índice, e engajamento e Deezer, que não entram e vão
 * rotulados como tal.
 *
 * Mora no núcleo porque são dois renderizadores — o PDF da web e o do núcleo — e a secção é a
 * mesma nos dois.
 */
export const plataformasExibidas = (
  ri: Diagnostico | null | undefined,
  cm?: Record<string, any> | null,
): LinhaDaPlataforma[] => {
  if (!ri || ehLegado(ri)) return [];
  const l = ri.components?.l ?? {};
  const playlists = l.playlists ?? {};
  const radio = l.radio ?? {};
  const execucoes = ri.raw?.radioAirplay180d;
  const quantasPlaylists = ri.raw?.editorialPlaylists ?? cm?.playlists?.count ?? null;
  const deezer = ri.deezerFans ?? null;
  return [
    {
      rotulo: 'Playlists editoriais',
      // Consulta vazia ≠ consulta que não aconteceu (§4). Dizer "0" nas duas apagava a diferença.
      valor: playlists.present ? String(quantasPlaylists ?? 0) : 'Sem dado',
    },
    {
      rotulo: 'Execução em rádio · 180 dias',
      // Abaixo de 6 execuções o componente é AUSENTE, não zero (§9.5).
      valor: radio.present && execucoes != null
        ? `${fmtNum(Math.round(Number(execucoes)))} execuções`
        : 'Sem dado',
    },
    ...engajamentoExibido(ri).map((e) => ({
      rotulo: `Engajamento no ${NOME_DA_REDE[e.rede] ?? e.rede}`,
      valor: fmtPct(e.value),
      informativo: true,
    })),
    { rotulo: 'Fãs no Deezer', valor: deezer == null ? 'Sem dado' : fmtNum(deezer), informativo: true },
  ];
};

export const engajamentoExibido = (ri: Diagnostico | null | undefined) => {
  const e = ri?.engagement ?? {};
  return (['instagram', 'tiktok', 'youtube'] as const)
    .map((rede) => (e[rede] ? { rede, ...e[rede] } : null))
    .filter(Boolean) as { rede: 'instagram' | 'tiktok' | 'youtube'; value: number; cut: number; above: boolean }[];
};
